import {isFeatureEnabled} from 'feature-flags';
import {DataStatus, Flag, Label, Parameters, Rank, Tooltip} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, Space, Text, VStack} from 'platform/foundation';
import {DateTimeFormat, useFormatCurrency, useFormatNumber} from 'platform/locale';

import {ReactElement} from 'react';

import {isNotNil} from 'ramda';

import {SourcingVehicleDetailResponseBody} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {
  CurrencyCodeType,
  EMPTY_PLACEHOLDER,
  getSafely,
  suffixTestId,
  TestIdProps,
} from 'shared/web';

import {decimalToPercentRank} from '../../utils/decimalToPercentRank';
import {detailSecondLine} from '../../utils/detailSecondLine';
import {getDetailFirstLine, getDetailFirstLineForTooltip} from '../../utils/getDetailFirstLine';
import {getLID} from '../../utils/getLID';
import {getVehicleDetailPosition} from '../../utils/getVehicleDetailPosition';
import {ActionButtonsVehicle} from '../ActionButtonsVehicle/ActionButtonsVehicle';
import {AttractivenessFlag} from '../AttractivenessFlag/AttractivenessFlag';
import {Clamp} from '../Clamp/Clamp';
import {CountryFlag} from '../CountryFlag/CountryFlag';
import {HitlistBlacklistFlag} from '../HitlistBlacklistFlag/HitlistBlacklistFlag';
import {PriceChangesPopper} from '../PriceChangesPopper/PriceChangesPopper';
import {createRowElementId} from '../SourcingTable/utils/createRowElementId';
import {getAdvertisedTime} from '../SourcingTable/utils/getAdvertisedTime';
import {convertVehicleTagsToVehicleHighlights} from '../VehicleHighlights/utils/convertVehicleTagsToVehicleHighlights';
import {VehicleHighlights} from '../VehicleHighlights/VehicleHighlights';
import {VehicleImage} from '../VehicleImage/VehicleImage';
import {Car} from './components/Car';

export interface AdvanceVehicleViewProps extends TestIdProps {
  index?: number;
  vehicle?: SourcingVehicleDetailResponseBody;
  isVehicleLoading?: boolean;
  onRowClick?: () => void;
  isLastClickedRow?: boolean;
  setLastVisitedVehicle?: (adId: string, index: number) => void;
  children?: ReactElement;
}

const PERCENTAGE_MULTIPLIER = 100;

export function AdvanceVehicleView(props: AdvanceVehicleViewProps) {
  const formatNumber = useFormatNumber();
  const formatCurrency = useFormatCurrency();

  const sourcingVehicle =
    props.vehicle && 'sourcingVehicle' in props.vehicle ? props.vehicle.sourcingVehicle : null;

  const vehiclePosition = getVehicleDetailPosition(
    sourcingVehicle?.extraData?.marketPosition,
    sourcingVehicle?.extraData?.marketPositionCount,
    sourcingVehicle?.pricePercentile
  );
  const originalPrice = sourcingVehicle?.price?.originalWithVat;
  const originalCurrency = sourcingVehicle?.price?.originalCurrency;
  const currency = sourcingVehicle?.price?.currency;
  const originalCurrencyCode = originalCurrency?.key as CurrencyCodeType;
  const convertedPrice = props.vehicle?.summary.original;
  const convertedCurrencyCode = currency?.key as CurrencyCodeType;
  const firstScrapedPrice = getSafely(sourcingVehicle?.priceChanges, 0) ?? convertedPrice;
  const suspiciousReasons = sourcingVehicle?.suspiciousReason
    ?.map((reason) => i18n.t(`entity.vehicle.labels.${reason}`))
    .join(', ');
  const {firstOccurrenceDateTime, shouldShowTime, firstOccurrenceDistance} = getAdvertisedTime(
    sourcingVehicle,
    formatNumber
  );

  return (
    <Car
      id={props.vehicle && createRowElementId(props.vehicle)}
      key={sourcingVehicle?.adId}
      // Legacy component
      // eslint-disable-next-line react/forbid-component-props
      className={props.isLastClickedRow ? 'active-iframe' : ''}
      isNew={props.vehicle?.isNew}
      isDisabled={
        sourcingVehicle?.isAvailable === false // isAvailable must be false not undefined
      }
      onClick={props.onRowClick}
      isLoading={!sourcingVehicle}
    >
      <VehicleImage vehicle={props.vehicle} borderRadius="medium" />
      <DataStatus isLoading={props.isVehicleLoading}>
        <Box padding={3}>
          <VStack spacing={2}>
            <HStack align="center" spacing={2} wrap="nowrap">
              <CountryFlag country={sourcingVehicle?.seller?.country?.key} />
              <Show when={sourcingVehicle?.isAvailable === false}>
                <Flag
                  label={i18n.t('entity.vehicle.labels.sold')}
                  size="small"
                  data-testid={suffixTestId('sold', props)}
                />
              </Show>
              <Show when={props.vehicle?.isNew}>
                <Box
                  backgroundColor="palettes.green.60.100"
                  width={3}
                  height={3}
                  borderRadius="circular"
                />
              </Show>
              <HStack align="center" spacing={2} grow={1}>
                <Tooltip label={sourcingVehicle && getDetailFirstLineForTooltip(sourcingVehicle)}>
                  <Clamp lines={1}>
                    <Heading size={4} data-testid={suffixTestId('vehicleName', props)}>
                      {sourcingVehicle && getDetailFirstLine(sourcingVehicle)}
                    </Heading>
                  </Clamp>
                </Tooltip>
                <Show when={props.vehicle?.vehicleSummary?.comment}>
                  <Tooltip placement="top" label={props.vehicle?.vehicleSummary?.comment}>
                    <Icon value="communication/comment" />
                  </Tooltip>
                </Show>
                <Show whenFeatureEnabled={featureFlags.SOURCING_HITLIST_BLACKLIST}>
                  <HitlistBlacklistFlag sourcingVehicle={sourcingVehicle} />
                </Show>
                <Show
                  whenFeatureEnabled={featureFlags.SOURCING_SUSPICIOUS_VEHICLES}
                  when={sourcingVehicle?.suspicious}
                >
                  <Tooltip label={suspiciousReasons}>
                    <Icon value="alert/warning" color="severity.warning" size={5} />
                  </Tooltip>
                </Show>
              </HStack>
              {props.vehicle && sourcingVehicle?.isAvailable && (
                <ActionButtonsVehicle
                  vehicle={props.vehicle}
                  onDetailOpen={() =>
                    props.setLastVisitedVehicle?.(sourcingVehicle?.adId, props.index ?? 0)
                  }
                  data-testid={suffixTestId('actionButtonsVehicle', props)}
                />
              )}
            </HStack>
            <Show when={isNotNil(sourcingVehicle)}>
              <Parameters
                size="small"
                color="secondary"
                parameters={detailSecondLine(formatNumber, sourcingVehicle)}
                data-testid={suffixTestId('vehicleParameters', props)}
              />
            </Show>
            <VehicleHighlights
              data={convertVehicleTagsToVehicleHighlights(
                [],
                [],
                Object.values(props.vehicle?.features ?? {}).filter((i) => i.isHighlight)
              )}
            />
            <HStack justify="space-between" spacing={2} wrap>
              <Box>
                <Label>{i18n.t('entity.vehicle.labels.position')}</Label>
                <Text size="xSmall">{vehiclePosition[0]}</Text>
                <Space vertical={1} />
                <Text size="xxSmall" color="tertiary">
                  {vehiclePosition[1]}
                </Text>
              </Box>
              <Box>
                <Label>{i18n.t('entity.vehicle.labels.originalPrice')}</Label>
                <HStack spacing={1} align="center">
                  {isNotNil(sourcingVehicle) && isNotNil(convertedPrice) && (
                    <PriceChangesPopper
                      vehicle={sourcingVehicle}
                      currentPrice={convertedPrice}
                      currency={convertedCurrencyCode}
                      popperPlacement="top-start"
                    />
                  )}
                  <Tooltip
                    label={
                      isNotNil(originalPrice)
                        ? formatCurrency(originalPrice, originalCurrencyCode)
                        : undefined
                    }
                  >
                    <Text size="xSmall">
                      {isNotNil(convertedPrice)
                        ? formatCurrency(convertedPrice, convertedCurrencyCode)
                        : EMPTY_PLACEHOLDER}
                    </Text>
                  </Tooltip>
                </HStack>
                <Show
                  when={firstScrapedPrice !== convertedPrice}
                  whenFeatureEnabled={featureFlags.SOURCING_PRICE_CHANGES}
                >
                  <Space vertical={1} />
                  <Text size="xxSmall" color="tertiary" strikethrough>
                    {isNotNil(firstScrapedPrice)
                      ? formatCurrency(firstScrapedPrice, convertedCurrencyCode)
                      : null}
                  </Text>
                </Show>
              </Box>
              <Box>
                <Label>{i18n.t('entity.vehicle.labels.margin')}</Label>
                <Text
                  size="xSmall"
                  color={(sourcingVehicle?.maximalPossibleMargin ?? 0) < 0 ? 'danger' : undefined}
                >
                  {isNotNil(sourcingVehicle?.maximalPossibleMargin)
                    ? formatCurrency(sourcingVehicle?.maximalPossibleMargin, convertedCurrencyCode)
                    : EMPTY_PLACEHOLDER}
                </Text>
              </Box>
              <Box>
                <Label>{i18n.t('entity.vehicle.labels.recommendedPrice')}</Label>
                <Text size="xSmall">
                  {isNotNil(sourcingVehicle?.extraData?.predictedPrice)
                    ? formatCurrency(
                        sourcingVehicle?.extraData?.predictedPrice,
                        convertedCurrencyCode
                      )
                    : EMPTY_PLACEHOLDER}
                </Text>
              </Box>
              <Box>
                <Label>{i18n.t('entity.vehicle.labels.LID')}</Label>
                <Text size="xSmall">
                  {isNotNil(props.vehicle?.summary?.lid)
                    ? formatNumber(getLID(props.vehicle?.summary?.lid), 0)
                    : EMPTY_PLACEHOLDER}
                </Text>
              </Box>
              <Box>
                <Label>{i18n.t('entity.vehicle.labels.DOD')}</Label>
                <Tooltip
                  label={
                    isFeatureEnabled(featureFlags.SOURCING_REAL_TIME_ADVERTISEMENT) ? (
                      <DateTimeFormat
                        value={firstOccurrenceDateTime}
                        format={shouldShowTime ? 'dateTimeShort' : 'dateShort'}
                      />
                    ) : undefined
                  }
                >
                  <Text
                    size="xSmall"
                    color={shouldShowTime ? 'success' : 'primary'}
                    noWrap
                    overflowWrap="anywhere"
                  >
                    {firstOccurrenceDistance}
                  </Text>
                </Tooltip>
              </Box>
              <Box>
                <Label>{i18n.t('entity.vehicle.labels.features')}</Label>
                <Tooltip label={decimalToPercentRank(props.vehicle?.summary?.rank ?? undefined)}>
                  <Rank
                    percentage={(props.vehicle?.summary?.rank ?? 0) * PERCENTAGE_MULTIPLIER}
                    data-testid={suffixTestId('rank', props)}
                  />
                </Tooltip>
              </Box>
              <Show whenFeatureEnabled={featureFlags.SOURCING_ATTRACTIVENESS_SCORE}>
                <Box>
                  <Label>{i18n.t('entity.vehicle.labels.attractiveness')}</Label>
                  <AttractivenessFlag sourcingVehicle={sourcingVehicle} />
                </Box>
              </Show>
            </HStack>
          </VStack>
        </Box>
      </DataStatus>
    </Car>
  );
}
